import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';
import {elementIdSchema} from '../../schemas';

const toolName = 'set-element-text-content' as const;

const argumentZod = z.object({
  elementId: elementIdSchema.describe(
    'The data-w-id of a text-bearing element (heading, paragraph, link, button, span). Must NOT be a list item, list, div, section, or other structural container — use render for those.'
  ),
  content: z
    .string()
    .describe(
      'The text content to set on the element. This will replace any existing text content.'
    ),
});

export const setElementTextContentTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Set element text content',
  prettyErrors: {
    fallback: 'Unable to set text content.',
    patterns: [
      {
        match: 'Component',
        message:
          'HARD STOP — This element is inside a Component Instance. DO NOT retry set-element-text-content. Instead: (1) Look at the prop-* attributes already visible on the <wf-component> tag from get-html-from-selected-element output — do NOT call read-page. (2) If a matching prop exists (e.g. prop-title, prop-text), use the render tool with MODIFY mode: <wf-component data-w-id="..." prop-name="new value"></wf-component>. (3) If no matching prop is visible but you need to discover all props, call list-file then read-file on the component definition. (4) If no prop exists for the content, tell the user it cannot be edited.',
      },
      {
        match: 'does not support text content',
        message: 'This element does not support text content.',
      },
      {
        match: 'not found',
        message: 'Element not found.',
      },
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Set text content on a text-bearing element (heading, paragraph, link, button, span).
      Pass the data-w-id of the element that directly owns the text — NOT a list item,
      list, div, section, or any other structural container. For those, use render
      instead. Does NOT work on Component Instances — component content is controlled
      through props, not direct text editing. Check the prop-* attributes on the
      <wf-component> tag, or use list-file and read-file to discover props, then use
      render with the component's prop-* attributes to update them. Returns null when
      text content is successfully set.`,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [{resource: 'SitePage', action: 'editContent'}],
  confirmBeforeExecuting: true, // Modifies element content, requires confirmation
  timeout: 30000, // 30 seconds for setting text content
});
